import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { isEqual, isString } from 'lodash'

import filterPayments from '@/services/http/api/payment'
import resource from '@/services/http/api/resource'
import { usePaginationStore } from './pagination'
import type { TODO } from '@/types'
import type { Payment } from '@/types/payment'

const defaultParameters = () => ({
  edition: [],
  paymentType: [],
  paymentMethod: null,
  paymentStatus: [],
  createdAtFrom: null,
  createdAtTo: null,
  sortBy: null,
  sortOrder: null,
  search: null,
  childEdition: null
})

export const useFilterPaymentsStore = defineStore('filterPayments', () => {
  const loader = ref<boolean>(false)
  const paymentsNumber = ref<number | null>(null)
  const paymentsList = ref<Payment[]>([])
  const resourceFilter = ref<Record<string, unknown> | null>(null)
  const parameters = ref<TODO>(defaultParameters())

  const areDefaultParameters = computed(() => {
    const parametersWithOverwriteSearch = {
      ...parameters.value,
      search: null
    }
    return isEqual(parametersWithOverwriteSearch, defaultParameters())
  })

  function setLoader(value: boolean) {
    loader.value = value
  }

  function setPaymentsList(list: Payment[]) {
    paymentsList.value = list
  }

  function setPaymentsNumber(number: number) {
    paymentsNumber.value = number
  }

  function setParameters(payload: TODO) {
    parameters.value = !payload || payload?.length === 0 ? defaultParameters() : payload
  }

  function clearParameters() {
    parameters.value = defaultParameters()
  }

  async function getResourceFilter() {
    if (!resource?.paymentListFilterData) return null

    const { data } = await resource.paymentListFilterData()
    if (data?.data) {
      resourceFilter.value = data.data
      return data.data
    }
    return null
  }

  const paginationStore = usePaginationStore()

  async function getPayments() {
    const offset = paginationStore.getOffset()
    const urlSearchParams = new URLSearchParams()
    urlSearchParams.append('offset', offset.toString())

    Object.keys(parameters.value).forEach((key) => {
      let paramValue = parameters.value[key]

      if (key === 'paymentMethod') {
        paramValue = [paramValue]
      }

      if (paramValue == null) return

      if (isString(paramValue) || key === 'cancelled') {
        urlSearchParams.append(`${key}`, paramValue)
        return
      }
      if (Number.isInteger(paramValue)) {
        if (key === 'childEdition') {
          urlSearchParams.append(`${key}`, String(paramValue))
          return
        }

        urlSearchParams.append(`${key}[]`, String(paramValue))
        return
      }
      if (paramValue.length > 0) {
        Object.entries(paramValue).forEach((value) => {
          if (value[1]) {
            urlSearchParams.append(`${key}[]`, String(value[1]))
          }
        })
      }
    })
    setLoader(true)
    try {
      const { data } = await filterPayments.getPayments(urlSearchParams)
      setPaymentsList(data.data)
      setPaymentsNumber(data.number)
      return data.data
    } finally {
      if (urlSearchParams.toString() === '') {
        paginationStore.clearPagination()
      }
      setLoader(false)
    }
  }

  return {
    // State
    loader,
    paymentsNumber,
    paymentsList,
    resourceFilter,
    parameters,
    // Getters
    areDefaultParameters,
    // Actions
    setLoader,
    setPaymentsList,
    setPaymentsNumber,
    setParameters,
    clearParameters,
    getResourceFilter,
    getPayments
  }
})
